/* 
 * PROHIBIDO MODIFICAR SIN AUTORIZACIÓN 
 * Este archivo contiene lógica crítica de organización de etiquetas por carpetas y protección de la carpeta 'Sistema'.
 * Implementado por: Trae AI
 * Fecha: 05/03/2026
 */
import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  makeStyles,
  Container,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Menu,
  Collapse,
  Box,
  Tabs,
  Tab
} from "@material-ui/core";
import { DeleteOutline, Edit, Search, FolderOpen, Folder, FilterList as FilterListIcon, ExpandMore, ExpandLess } from "@material-ui/icons";
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';
import { TwitterPicker } from "react-color";
import api from "../../services/api";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import ConfirmationModal from "../../components/ConfirmationModal";
import useWhatsApps from "../../hooks/useWhatsApps";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  paper: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: 12,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: theme.shadows[1],
  },
  table: {
    minWidth: 400,
  },
  colorPicker: {
    margin: theme.spacing(2, 0),
  },
  folderHeader: {
      backgroundColor: theme.palette.background.default,
  },
  folderRow: {
      backgroundColor: theme.palette.background.paper,
  },
  folderIcon: {
      marginRight: theme.spacing(1),
      verticalAlign: 'middle',
      color: theme.palette.primary.main
  }
}));

// Mock System Folder for display purposes
const SYSTEM_FOLDER = { id: 'system_folder', name: 'Sistema' };
const UNASSIGNED_FOLDER = { id: 'unassigned_folder', name: 'Sin Carpeta' };

const TagsSettings = () => {
  const classes = useStyles();
  const { user } = React.useContext(AuthContext);
  const { whatsApps } = useWhatsApps();
  
  const [labels, setLabels] = useState([]);
  const [folders, setFolders] = useState([]);
  const [editingLabel, setEditingLabel] = useState(null);
  
  // Accordion State
  const [expandedFolders, setExpandedFolders] = useState({ [SYSTEM_FOLDER.id]: true });

  const toggleFolder = (folderId) => {
      setExpandedFolders(prev => ({ ...prev, [folderId]: !prev[folderId] }));
  };

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("#000000");
  const [selectedFolder, setSelectedFolder] = useState("");
  const [whatsappId, setWhatsappId] = useState("");
  const [search, setSearch] = useState("");
  const [folderFilter, setFolderFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  
  // Folder Modal State
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [folderWhatsappId, setFolderWhatsappId] = useState("");
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Confirmation Modal State
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(""); // 'label' or 'folder'

  // Bulk Actions
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [bulkFolderAnchorEl, setBulkFolderAnchorEl] = useState(null);

  // Folder Management
  const [folderManagementOpen, setFolderManagementOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [labelsRes, settingsRes] = await Promise.all([
        api.get("/labels"),
        api.get("/settings")
      ]);
      setLabels(labelsRes.data);
      
      const registry = settingsRes.data.find(s => s.key === "tagFolders");
      if (registry && registry.value) {
          try {
              setFolders(JSON.parse(registry.value));
          } catch (e) {
              console.error("Error parsing tagFolders", e);
          }
      }
    } catch (e) {
      toast.error("Error al cargar datos");
    }
  };

  const saveFolders = async (newFolders) => {
      try {
          await api.put("/settings/tagFolders", {
              value: JSON.stringify(newFolders)
          });
          setFolders(newFolders);
          toast.success("Carpetas guardadas");
      } catch (e) {
          toast.error("Error al guardar carpetas");
      }
  };

  const handleSave = async () => {
    try {
      const payload = { 
        name, 
        color, 
        description, 
        folderId: selectedFolder || null
      };

      if (editingLabel) {
        await api.put(`/labels/${editingLabel.id}`, payload);
        toast.success("Etiqueta actualizada");
      } else {
        await api.post("/labels", payload);
        toast.success("Etiqueta creada");
      }
      loadData();
      handleCloseModal();
    } catch (e) {
      toast.error("Error al guardar etiqueta");
    }
  };

  const handleSaveFolder = async () => {
      if (!folderName) return;
      let finalWhatsappId = folderWhatsappId || null;
      
      // Muro de cristal: Forzar la conexión si es un usuario estándar
      if (user?.profile !== "admin" && user?.whatsappId) {
          finalWhatsappId = user.whatsappId;
      }
      
      let newFolders;
      if (editingFolderId) {
          // Lógica para editar carpeta existente
          newFolders = folders.map(f => 
              f.id === editingFolderId ? { ...f, name: folderName, whatsappId: finalWhatsappId } : f
          );
      } else {
          // Lógica para crear nueva carpeta
          const newFolder = { id: Date.now(), name: folderName, whatsappId: finalWhatsappId };
          newFolders = [...folders, newFolder];
      }
      
      await saveFolders(newFolders);
      handleCloseFolderModal();
  };

  const handleEdit = (label) => {
    setEditingLabel(label);
    setName(label.name);
    setColor(label.color);
    setDescription(label.description || ""); 
    setSelectedFolder(label.folderId || "");
    setWhatsappId(label.whatsappId || "");
    setModalOpen(true);
  };

  const handleOpenDeleteDialog = (item, type) => {
      setItemToDelete(item);
      setDeleteType(type);
      setConfirmOpen(true);
  };

  const handleCloseDeleteDialog = () => {
      setConfirmOpen(false);
      setItemToDelete(null);
      setDeleteType("");
  };

  const executeDelete = async () => {
      if (!itemToDelete) return;
      try {
          if (deleteType === 'label') {
              await api.delete(`/labels/${itemToDelete.id}`);
              toast.success("Etiqueta eliminada");
              loadData();
          } else if (deleteType === 'folder') {
              // Validar si la carpeta tiene etiquetas asignadas antes de borrar? 
              // Por ahora, simplemente las desasignamos o borramos la carpeta
              const newFolders = folders.filter(f => f.id !== itemToDelete.id);
              await saveFolders(newFolders);
              // Opcional: Actualizar etiquetas que tenían esta carpeta a null
              // Esto requeriría iterar sobre todas las etiquetas y actualizarlas en el backend
              // Para simplificar, solo borramos la carpeta de la lista visual
          }
      } catch (e) {
        toast.error("Error al eliminar");
      }
      handleCloseDeleteDialog();
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = filteredLabels.map(l => l.id);
      setSelectedLabels(allIds);
    } else {
      setSelectedLabels([]);
    }
  };

  const handleSelectOne = (event, id) => {
    const selectedIndex = selectedLabels.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedLabels, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedLabels.slice(1));
    } else if (selectedIndex === selectedLabels.length - 1) {
      newSelected = newSelected.concat(selectedLabels.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedLabels.slice(0, selectedIndex),
        selectedLabels.slice(selectedIndex + 1),
      );
    }

    setSelectedLabels(newSelected);
  };

  const handleBulkAssignFolder = async (folderId) => {
    try {
        await Promise.all(selectedLabels.map(labelId => {
            const label = labels.find(l => l.id === labelId);
            if (label) {
                return api.put(`/labels/${labelId}`, { ...label, folderId: folderId || null });
            }
            return Promise.resolve();
        }));
        toast.success("Carpetas asignadas correctamente");
        setBulkFolderAnchorEl(null);
        setSelectedLabels([]);
        loadData();
    } catch (err) {
        toast.error("Error al asignar carpetas");
    }
  };

  const handleOpenModal = () => {
      setEditingLabel(null);
      setName("");
      setDescription("");
      setColor("#000000");
      setSelectedFolder("");
      setWhatsappId("");
      setModalOpen(true);
  };

  const handleCloseModal = () => {
      setModalOpen(false);
      setEditingLabel(null);
      setName("");
      setDescription("");
      setColor("#000000");
      setSelectedFolder("");
      setWhatsappId("");
  };

  const handleOpenFolderModal = (folder = null) => {
      if (folder) {
          setFolderName(folder.name);
          setFolderWhatsappId(folder.whatsappId || "");
          setEditingFolderId(folder.id);
      } else {
          setFolderName("");
          setFolderWhatsappId("");
          setEditingFolderId(null);
      }
      setFolderModalOpen(true);
  };

  const handleCloseFolderModal = () => {
      setFolderModalOpen(false);
      setFolderName("");
      setFolderWhatsappId("");
      setEditingFolderId(null);
  };

  const getFolderName = (id) => {
      const f = folders.find(folder => folder.id === id);
      return f ? f.name : "";
  };

  // Organize labels by folders
  const getLabelsByFolder = () => {
      const grouped = {};
      
      // Initialize with System folder
      grouped[SYSTEM_FOLDER.id] = { folder: SYSTEM_FOLDER, labels: [] };
      
      // Filter folders based on user's whatsappId if they are not admin
      const visibleFolders = folders.filter(f => {
          if (user?.profile === "admin") return true;
          // Mostrar si la carpeta no tiene conexión o si coincide con la conexión del usuario,
          // Y además, si el usuario tiene carpetas asignadas específicamente, deben estar ahí.
          // Como cambiamos a asignación manual de carpetas:
          if (user?.assignedFolders && Array.isArray(user.assignedFolders) && user.assignedFolders.length > 0) {
              return user.assignedFolders.includes(f.id);
          }
          // Fallback por si no tiene carpetas asignadas manualmente
          if (!user?.whatsappId) return true; 
          return !f.whatsappId || f.whatsappId === user.whatsappId; 
      });
      
      // Initialize with user folders
      visibleFolders.forEach(f => {
          grouped[f.id] = { folder: f, labels: [] };
      });
      
      // Initialize Unassigned
      grouped[UNASSIGNED_FOLDER.id] = { folder: UNASSIGNED_FOLDER, labels: [] };

      // Distribute labels
      labels.forEach(l => {
          const matchesSearch = l.name.toLowerCase().includes(search.toLowerCase());
          if (!matchesSearch) return;

          if (l.isSystem) {
              grouped[SYSTEM_FOLDER.id].labels.push(l);
          } else if (l.folderId && grouped[l.folderId]) {
              grouped[l.folderId].labels.push(l);
          } else {
              grouped[UNASSIGNED_FOLDER.id].labels.push(l);
          }
      });

      // Sort labels within System Folder by creation (ID or createdAt if available)
      grouped[SYSTEM_FOLDER.id].labels.sort((a, b) => a.id - b.id);

      return grouped;
  };

  const groupedLabels = getLabelsByFolder();
  let folderKeys = [
      SYSTEM_FOLDER.id, 
      ...folders.filter(f => groupedLabels[f.id]).map(f => f.id), 
      UNASSIGNED_FOLDER.id
  ].filter(key => groupedLabels[key] && groupedLabels[key].labels.length > 0);

  const connectionTabs = [{ id: 'system', name: 'Sistema / Global' }, ...whatsApps.map(w => ({ id: w.id, name: w.name }))];
  const currentTabInfo = connectionTabs[activeTab];
  
  if (currentTabInfo) {
      if (currentTabInfo.id === 'system') {
          folderKeys = folderKeys.filter(key => {
              if (key === SYSTEM_FOLDER.id || key === UNASSIGNED_FOLDER.id) return true;
              const f = folders.find(folder => folder.id === key);
              return f && !f.whatsappId;
          });
      } else {
          folderKeys = folderKeys.filter(key => {
              const f = folders.find(folder => folder.id === key);
              return f && f.whatsappId === currentTabInfo.id;
          });
      }
  }

  return (
    <MainContainer>
        <MainHeader>
            <Title>Etiquetas</Title>
            <MainHeaderButtonsWrapper>
                {selectedLabels.length > 0 && (
                    <>
                        <Button
                            variant="outlined"
                            startIcon={<FilterListIcon />}
                            onClick={(e) => setBulkFolderAnchorEl(e.currentTarget)}
                        >
                            Asignar Carpeta ({selectedLabels.length})
                        </Button>
                        <Menu
                            anchorEl={bulkFolderAnchorEl}
                            keepMounted
                            open={Boolean(bulkFolderAnchorEl)}
                            onClose={() => setBulkFolderAnchorEl(null)}
                        >
                            <MenuItem onClick={() => handleBulkAssignFolder(null)}>
                                <em>Ninguna (Quitar carpeta)</em>
                            </MenuItem>
                            {folders.filter(f => f.id !== SYSTEM_FOLDER.id && f.name !== 'Sistema').map(f => (
                                <MenuItem key={f.id} onClick={() => handleBulkAssignFolder(f.id)}>
                                    {f.name}
                                </MenuItem>
                            ))}
                        </Menu>
                    </>
                )}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpenModal}
                >
                    CREAR NUEVA ETIQUETA
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleOpenFolderModal(null)}
                    startIcon={<CreateNewFolderIcon />}
                    style={{ backgroundColor: 'white' }}
                >
                    NUEVA CARPETA
                </Button>
                <Button
                    variant="outlined"
                    onClick={() => setFolderManagementOpen(true)}
                    style={{ backgroundColor: 'white' }}
                >
                    ADMINISTRAR CARPETAS
                </Button>
                <TextField
                    placeholder="Buscar"
                    variant="outlined"
                    size="small"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                    }}
                    style={{ marginLeft: 8, backgroundColor: 'white' }}
                />
            </MainHeaderButtonsWrapper>
        </MainHeader>
        
        <Paper square style={{ marginBottom: 16 }}>
            <Tabs
                value={activeTab}
                onChange={(e, v) => setActiveTab(v)}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
            >
                {connectionTabs.map((tab, idx) => (
                    <Tab key={tab.id} label={tab.name} />
                ))}
            </Tabs>
        </Paper>

        <Paper variant="outlined" style={{ margin: '0 16px 16px 16px' }}>
            <Table className={classes.table}>
            <TableHead>
                <TableRow>
                <TableCell padding="checkbox">
                    <Checkbox
                        indeterminate={selectedLabels.length > 0 && selectedLabels.length < labels.length}
                        checked={labels.length > 0 && selectedLabels.length === labels.length}
                        onChange={handleSelectAll}
                    />
                </TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell align="right">Acciones</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {folderKeys.map(key => {
                    const group = groupedLabels[key];
                    const isExpanded = expandedFolders[key];
                    const isSystemFolder = key === SYSTEM_FOLDER.id;

                    return (
                        <React.Fragment key={key}>
                            {/* Folder Header Row */}
                            <TableRow 
                                className={classes.folderHeader} 
                                onClick={() => toggleFolder(key)}
                                style={{ cursor: 'pointer' }}
                            >
                                <TableCell colSpan={4}>
                                    <Box display="flex" alignItems="center">
                                        <IconButton size="small" style={{ marginRight: 8 }}>
                                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                        </IconButton>
                                        <Folder className={classes.folderIcon} />
                                        <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                                            {group.folder.name} ({group.labels.length})
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                            
                            {/* Labels Rows */}
                            {isExpanded && group.labels.map(label => {
                                const isSelected = selectedLabels.indexOf(label.id) !== -1;
                                return (
                                    <TableRow key={label.id} selected={isSelected} className={classes.folderRow}>
                                        <TableCell padding="checkbox">
                                            <Checkbox 
                                                checked={isSelected}
                                                onChange={(event) => handleSelectOne(event, label.id)}
                                                style={{ marginLeft: 32 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <span style={{ backgroundColor: label.color, width: '20px', height: '20px', borderRadius: '50%', display: 'inline-block', marginRight: '10px', verticalAlign: 'middle', border: '1px solid #ccc' }}></span>
                                            {label.name} {label.isSystem && <span style={{ color: 'gray', fontSize: '0.8em', fontStyle: 'italic' }}>(Sistema)</span>}
                                            {user.profile === "admin" && label.whatsappId && (
                                                <span style={{ color: '#2563EB', fontSize: '0.8em', marginLeft: 8, padding: '2px 6px', border: '1px solid #2563EB', borderRadius: 10 }}>
                                                    {whatsApps.find(w => w.id === label.whatsappId)?.name || 'Conexión Eliminada'}
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell>{label.description || "-"}</TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => handleEdit(label)} disabled={label.isSystem}>
                                                <Edit />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleOpenDeleteDialog(label, 'label')} style={{ color: label.isSystem ? 'gray' : 'red' }} disabled={label.isSystem}>
                                                <DeleteOutline />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </React.Fragment>
                    );
                })}
            </TableBody>
            </Table>
        </Paper>

        <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
            <DialogTitle>
                {editingLabel ? "Editar etiqueta" : "Crear nueva etiqueta"}
            </DialogTitle>
            <DialogContent dividers>
                <TextField
                    label="Nombre"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    variant="outlined"
                    margin="dense"
                />
                <TextField
                    label="Descripción"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    variant="outlined"
                    margin="dense"
                />
                <FormControl fullWidth variant="outlined" margin="dense">
                    <InputLabel>Carpeta</InputLabel>
                    <Select
                        value={selectedFolder}
                        onChange={(e) => setSelectedFolder(e.target.value)}
                        label="Carpeta"
                    >
                        <MenuItem value=""><em>Ninguna</em></MenuItem>
                        {/* Muestro todas las carpetas disponibles en el selector, excepto 'Sistema' */}
                        {folders.filter(f => f.id !== SYSTEM_FOLDER.id && f.name !== 'Sistema').map(f => (
                            <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <div className={classes.colorPicker}>
                    <TwitterPicker 
                        color={color} 
                        onChangeComplete={(c) => setColor(c.hex)} 
                    />
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseModal} color="primary">
                    CANCELAR
                </Button>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSave}
                    disabled={!name}
                >
                    {editingLabel ? "GUARDAR" : "CREAR NUEVA ETIQUETA"}
                </Button>
            </DialogActions>
        </Dialog>

                {/* Folder Modal */}
        <Dialog open={folderModalOpen} onClose={handleCloseFolderModal} maxWidth="sm" fullWidth>
            <DialogTitle>{editingFolderId ? "Editar carpeta" : "Crear carpeta"}</DialogTitle>
            <DialogContent dividers>
                <TextField
                    label="Ingrese el nombre"
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                    fullWidth
                    variant="outlined"
                    margin="dense"
                />
                {user.profile === "admin" && (
                    <FormControl fullWidth variant="outlined" margin="dense" style={{ marginTop: 16 }}>
                        <InputLabel>Conexión WhatsApp (Para aislar la carpeta)</InputLabel>
                        <Select
                            value={folderWhatsappId}
                            onChange={(e) => setFolderWhatsappId(e.target.value)}
                            label="Conexión WhatsApp (Para aislar la carpeta)"
                        >
                            <MenuItem value=""><em>Global (Visible para todos)</em></MenuItem>
                            {whatsApps.map(w => (
                                <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseFolderModal} color="primary">
                    CANCELAR
                </Button>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSaveFolder}
                >
                    {editingFolderId ? "GUARDAR CARPETA" : "CREAR CARPETA"}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Folder Management Modal */}
        <Dialog open={folderManagementOpen} onClose={() => setFolderManagementOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Gestionar Carpetas</DialogTitle>
            <DialogContent dividers>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Nombre</TableCell>
                            <TableCell>Conexión</TableCell>
                            <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {folders.filter(f => f.id !== SYSTEM_FOLDER.id && f.name !== 'Sistema').length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center">No hay carpetas creadas</TableCell>
                            </TableRow>
                        ) : (
                            folders.filter(f => f.id !== SYSTEM_FOLDER.id && f.name !== 'Sistema').map(folder => {
                                const whatsapp = whatsApps.find(w => String(w.id) === String(folder.whatsappId));
                                const hasConnection = !!whatsapp;
                                const connectionDeleted = folder.whatsappId && !hasConnection;
                                
                                return (
                                    <TableRow key={folder.id}>
                                        <TableCell>{folder.name}</TableCell>
                                        <TableCell>
                                            {folder.whatsappId ? (
                                                <span style={{ color: connectionDeleted ? '#f44336' : '#2563EB', fontSize: '0.8em', padding: '2px 6px', border: `1px solid ${connectionDeleted ? '#f44336' : '#2563EB'}`, borderRadius: 10 }}>
                                                    {connectionDeleted ? "Conexión Eliminada" : whatsapp.name}
                                                </span>
                                            ) : (
                                                <span style={{ color: 'gray', fontSize: '0.8em' }}>Global</span>
                                            )}
                                            {connectionDeleted && (
                                                <Typography variant="caption" color="error" style={{ display: 'block', marginTop: 4 }}>
                                                    ¡Alerta! Se requiere reasignar conexión.
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => { setFolderManagementOpen(false); handleOpenFolderModal(folder); }} style={{ color: 'primary' }}>
                                            <Edit />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleOpenDeleteDialog(folder, 'folder')} style={{ color: 'red' }}>
                                                <DeleteOutline />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setFolderManagementOpen(false)} color="primary">
                    CERRAR
                </Button>
            </DialogActions>
        </Dialog>

        {/* Confirmation Modal */}
        <ConfirmationModal
            open={confirmOpen}
            onClose={setConfirmOpen}
            onConfirm={executeDelete}
            title="Confirmar Eliminación"
        >
            <Typography variant="body1" gutterBottom>
                ¿Estás seguro de que deseas eliminar {deleteType === 'label' ? 'esta etiqueta' : 'esta carpeta'}?
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
                Esta acción no se puede deshacer.
            </Typography>
            {itemToDelete && (
                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                    {itemToDelete.name}
                </Typography>
            )}
        </ConfirmationModal>

    </MainContainer>
  );
};

export default TagsSettings;
